import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Block, VerdictBadge } from './PrimaNotaBlocks';
import type { Verdict } from './prima-nota-derived';
import type { PageMessage } from './prima-nota-actions';
import type { ProducedRegister } from './prima-nota-model';

type GeneratePanelProps = {
  monthName: string;
  produced: ProducedRegister | null;
  verdict: Verdict;
  generable: boolean;
  canGenerate: boolean;
  reviewed: boolean;
  onReviewedChange: (reviewed: boolean) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
  locked: boolean;
  generating: boolean;
  message: PageMessage | null;
  onGenerate: () => void;
};

function PanelAside({ produced, verdict }: Pick<GeneratePanelProps, 'produced' | 'verdict'>) {
  const { t } = useTranslation('finance');
  if (produced === null) {
    return <VerdictBadge verdict={verdict} />;
  }
  return (
    <span className={produced.vouchable ? 'status-badge success' : 'status-badge warning'}>
      {t('primaNota.panel.produced')}
    </span>
  );
}

function generateLabel(
  t: TFunction<'finance'>,
  generating: boolean,
  produced: ProducedRegister | null,
): string {
  if (generating) {
    return t('primaNota.generate.running');
  }
  return produced === null ? t('primaNota.generate.action') : t('primaNota.generate.again');
}

function MessageLine({ message }: { message: PageMessage }) {
  return (
    <p
      className={
        message.kind === 'ok'
          ? 'bg-success-tint text-success rounded-md px-3 py-2 text-sm leading-6'
          : 'bg-error-tint text-destructive rounded-md px-3 py-2 text-sm leading-6'
      }
      data-testid="prima-nota-message"
      role="status"
    >
      {message.text}
    </p>
  );
}

/** The panel that stays beside the work: confirm the month, add notes, produce the register. */
export function GeneratePanel(props: GeneratePanelProps) {
  const { produced, generable, reviewed, locked, message } = props;
  const { t } = useTranslation('finance');
  return (
    <Block
      id="prima-nota-generate"
      title={t('primaNota.panel.heading', { month: props.monthName })}
      aside={<PanelAside produced={produced} verdict={props.verdict} />}
    >
      <div className="space-y-1">
        <label className="flex items-start gap-2.5 text-sm font-semibold">
          <input
            type="checkbox"
            checked={reviewed}
            onChange={(event) => props.onReviewedChange(event.target.checked)}
            data-testid="prima-nota-reviewed"
            className="accent-link mt-0.5 size-4"
          />
          <span>{t('primaNota.generate.reviewedLabel')}</span>
        </label>
        <p className="text-muted-foreground pl-[26px] text-xs leading-5">
          {t('primaNota.generate.reviewedHint')}
        </p>
      </div>

      <div className="space-y-2">
        <label className="field-label" htmlFor="prima-nota-notes">
          {t('primaNota.generate.notesLabel')}
        </label>
        <textarea
          id="prima-nota-notes"
          value={props.notes}
          rows={3}
          onChange={(event) => props.onNotesChange(event.target.value)}
          className="border-input bg-card focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
        />
        <p className="text-muted-foreground text-xs">{t('primaNota.generate.notesHint')}</p>
      </div>

      <div className="space-y-2">
        <Button
          variant={produced === null ? 'default' : 'outline'}
          className="w-full"
          disabled={!generable || !reviewed || locked}
          onClick={props.onGenerate}
          data-testid="prima-nota-generate"
        >
          {generateLabel(t, props.generating, produced)}
        </Button>
        {!generable && (
          <p className="text-muted-foreground text-[13px]">{t('primaNota.generate.blocked')}</p>
        )}
        {props.canGenerate && !reviewed && (
          <p className="text-muted-foreground text-[13px]">{t('primaNota.generate.needsReview')}</p>
        )}
        {/* In the panel, which stays in view beside the work: the outcome of «Genera» and of
            an upload alike is read where the eye already is, not in a card scrolled away. */}
        {message !== null && <MessageLine message={message} />}
        <p className="text-muted-foreground text-xs leading-5">{t('primaNota.refresh.hint')}</p>
      </div>
    </Block>
  );
}
